import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { useContext, useRef, useState } from "react";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import workspaceService from "api/workspaceService";
import { IWorkspaceMember } from "dto/IWorkspace";
import CloseIcon from "assets/icons/close.svg?react";
import Button from "components/Button";
import { readAsBase64 } from "utils/readAsBase64";
import { getUserId } from "utils/getUserId";
import { useAuth } from "react-oidc-context";
import messagesService from "api/messagesService";
import PackagesContext from "contexts/PackagesContext";
import KeysContext from "contexts/KeysContext";
import { useOnClickOutside } from "usehooks-ts";
import { reEncrypt } from "utils/reEncrypt";
import { Editor } from "@tinymce/tinymce-react";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import "./tinymce.css";
import useFiles from "hooks/useFiles";
import { ItemKind } from "dto/IKindItems";
import { IPackage } from "dto/IPackage";
import ModalsManagerContext from "contexts/ModalsManager";
import SearchMemberInWorkspace from "components/SearchMemberInWorkspace";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import { useTranslation } from "react-i18next";

export const RedirectModal: FC<Props> = () => {
  const { t } = useTranslation();
  const { modals, setModals } = useContext(ModalsManagerContext);
  const pack = localStorage.getItem("package")
    ? (JSON.parse(localStorage.getItem("package")) as IPackage)
    : null;
  const Case = localStorage.getItem("case")
    ? (JSON.parse(localStorage.getItem("case")) as ICaseSimple)
    : null;

  const auth = useAuth();
  const userId = getUserId(auth.user!);
  const { mutate: redirect } = messagesService.useRedirect();
  const [workspaceId] = useState(getWorkspaceId() ?? "");
  workspaceService.useGetWorkspace(workspaceId);
  const [workspaceMember, setWorkspaceMember] =
    useState<IWorkspaceMember>(null);

  const { keys } = useContext(KeysContext);
  const [showModal, setShowModal] = useState(false);
  const outsideRef = useRef(null);

  useFiles();

  useOnClickOutside(outsideRef, () => {
    if (showModal) {
      setShowModal(false);
    }
  });

  const [content, setContent] = useState("");

  const { packages, setPackages } = useContext(PackagesContext);

  const onCancel = () => {
    setModals({ ...modals, hide: true });
  };

  const handleRedirect = async () => {
    let id: null | string = null;
    let Kind: null | ItemKind = null;
    let encryptionKeyId: string | null = null;
    if (pack) {
      id = pack.Id;
      Kind = pack.Kind;
      encryptionKeyId = pack.EncryptionKeyId;
    }

    if (Case) {
      id = Case.Id;
      Kind = Case.Kind;
    }

    if (!encryptionKeyId && workspaceMember) {
      redirect(
        {
          Id: id!,
          Kind: Kind!,
          ToUserId: workspaceMember.UserId,
          Content: content,
        },
        {
          onSuccess: () => {
            setModals({ ...modals, hide: true });

            if (pack) {
              const newPacks = [...packages];
              const newPack = newPacks.filter((p) => p.Id !== pack.Id);
              setPackages(newPack);
            }
          },
        },
      );
      return;
    }

    if (!encryptionKeyId) return;

    //const privateKey = await generateNewKey(encryptionKeyId, myKeys, keys);
    const encryptionKey = keys.find((k) => k?.Id === encryptionKeyId);

    const uint = await reEncrypt(userId, workspaceMember.UserId, encryptionKey);
    const readyToSend = await readAsBase64(uint);

    if (workspaceMember) {
      redirect(
        {
          Id: id!,
          Kind: Kind!,
          ToUserId: workspaceMember.UserId,
          //Title: title,
          Content: content,
          Keys: [{ Id: encryptionKeyId!, Key: readyToSend }],
        },
        {
          onSuccess: () => {
            if (pack) {
              const newPacks = [...packages];
              const newPack = newPacks.filter((p) => p.Id !== pack.Id);
              setPackages(newPack);
            }

            setModals({ ...modals, hide: true });
          },
        },
      );
    }
  };

  return (
    <ModalLayout
      name="createPost"
      dock={DockPosition.DISABLED}
      off={true}
      customX={8}
      customY={0}
      ignore
      isAnimation
      fullHeight={true}
      widthAsLeftArea={true}
      disableMove={true}
    >
      <div className={`${styles.wrapper}`}>
        <div className={styles.title}>
          {t("packages.internalRedirect")}
          <div className={styles.icons}>
            <CloseIcon
              className={styles.closeIcon}
              onClick={() => onCancel()}
            />
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.contentWrapper}>
            <div className={styles.inputs}>
              <SearchMemberInWorkspace
                label={t("packages.toLabel")}
                onChoose={(w) => setWorkspaceMember(w)}
              />
            </div>
            <div className={styles.editor}>
              <Editor
                apiKey="12k7ybb0o4007p3nah1s77v7gmrbalfhy4jreucu2nky2l3b"
                onInit={(evt, editor) => {}}
                onEditorChange={(text) => setContent(text)}
                initialValue=""
                value={content}
                init={{
                  forced_root_block: "div",
                  height: "100%",
                  menubar: false,
                  statusbar: false,
                  resize: false,
                  browser_spellcheck: true,
                  plugins: [
                    "advlist",
                    "autolink",
                    "lists",
                    "link",
                    "image",
                    "preview",
                    "anchor",
                    "searchreplace",
                    "visualblocks",
                    "code",
                    "fullscreen",
                    "insertdatetime",
                    "media",
                    "table",
                    "code",
                    "help",
                    "imageTools",
                    "quickbars",
                  ],
                  toolbar_persist: true,
                  fixed_toolbar_container_target: document.body,
                  contextmenu: "table",
                  contextmenu_never_use_native: true,
                  images_upload_url: "",
                  images_upload_handler: (): Promise<any> => {
                    return new Promise((resolve) => {
                      resolve(true);
                    });
                  },
                  toolbar: `bold italic underline | alignleft aligncenter alignright alignjustify | forecolor backcolor | indent outdent | bullist numlist | table`,
                  content_style:
                    ".mce-content-body { background: #F4EFF4;} .tox-editor-header { background: #F4EFF4!important;} .tox .tox-tbtn svg {fill: #ff0000!important;}",
                }}
              />
            </div>
          </div>

          <div className={styles.footer}>
            <div className={styles.button}>
              <Button
                loading={false}
                className={styles.btn2}
                disabled={false}
                onClick={() => {
                  handleRedirect();
                }}
                // text="Wyślij"
                element={<>{t("common.forward")}</>}
              />
            </div>
          </div>
        </div>
      </div>
    </ModalLayout>
  );
};
